import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "What should I watch tonight?",
  "What's free right now?",
  "Tell me about premium",
];

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Welcome to It's My Network 🎬 I'm your concierge — ask me what to watch, what's free, or about premium.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(
    "imn_" + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, I couldn't reach the network. Please try again.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-btn"
        className="fixed bottom-6 right-6 z-[950] h-14 w-14 rounded-full bg-white text-black flex items-center justify-center shadow-2xl"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        aria-label="Open concierge chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed bottom-24 right-6 z-[950] w-[calc(100vw-3rem)] max-w-sm h-[520px] flex flex-col border border-white/10 bg-[#0A0A0A] backdrop-blur-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <div className="h-9 w-9 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center">
                <Sparkles size={16} className="text-gold" />
              </div>
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-widest">IMN Concierge</p>
                <p className="font-mono-imn text-[10px] tracking-widest text-white/40 uppercase">
                  Powered by AI
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto hide-scrollbar px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-msg-${m.role}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-white text-black"
                        : "bg-white/5 border border-white/10 text-white/90"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="chat-suggestion"
                    className="text-[11px] border border-white/15 px-3 py-1.5 text-white/70 hover:bg-white/10 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 p-3 border-t border-white/10">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask the concierge…"
                className="flex-1 bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="chat-send-btn"
                className="h-10 w-10 bg-white text-black flex items-center justify-center hover:bg-white/90 active:scale-95 transition-all disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
